import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link, useLocation } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { User, LogOut, Shield, Star, Settings, Trash2, ChevronRight, Send, CalendarHeart, ClipboardList, Ticket, LifeBuoy, ArrowLeftRight } from "lucide-react"
import { useWorkspaceMode } from "@/hooks/useWorkspaceMode"
import { toast } from "sonner"
import { ProfilePictureUpload } from "@/components/ProfilePictureUpload"
import { getRoleLabel } from "@/lib/roleLabel"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

interface Profile {
  id: string
  email: string
  first_name: string
  role: string
  avatar_url?: string | null
  is_business_owner?: boolean
  created_at?: string
  updated_at?: string
}

interface UserRole {
  role: 'admin' | 'moderator' | 'user'
}

interface UserProfileProps {
  userId: string
  onNavigate?: () => void
}

const MenuLink = ({ to, icon: Icon, label, onClick }: { to: string; icon: React.ElementType; label: string; onClick?: () => void }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-muted/80 transition-colors group no-underline"
  >
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <span className="text-sm font-medium text-foreground">{label}</span>
    </div>
    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
  </Link>
)

// Distinct from the plain menu rows: reads as a workspace control, not another page link.
const WorkspaceSwitch = ({ to, label, hint, onClick }: { to: string; label: string; hint: string; onClick?: () => void }) => (
  <Link
    to={to}
    onClick={onClick}
    className="group flex items-center gap-3 rounded-xl border border-primary/25 bg-primary/5 px-3 py-2.5 no-underline transition-colors hover:bg-primary/10"
  >
    <div className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
      <ArrowLeftRight className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-primary leading-tight">{label}</span>
      <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{hint}</span>
    </div>
    <ChevronRight className="h-4 w-4 text-primary shrink-0" />
  </Link>
)

export const UserProfile = ({ userId, onNavigate }: UserProfileProps) => {
  const { canUseBusiness, setMode } = useWorkspaceMode()
  const inBusiness = useLocation().pathname.startsWith('/business')
  const [profileOverride, setProfileOverride] = useState<Profile | null>(null)
  const { data: fetchedProfile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile-full', userId],
    enabled: !!userId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single()
      if (error) throw error
      return data as Profile
    },
  })
  const { data: rolesData = [] } = useQuery({
    queryKey: ['user-roles', userId],
    enabled: !!userId,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async () => {
      const { data, error } = await supabase.from('user_roles').select('role').eq('user_id', userId)
      if (error) throw error
      return (data || []) as UserRole[]
    },
  })
  const profile = profileOverride ?? fetchedProfile ?? null
  const userRoles = rolesData
  const loading = profileLoading && !profile
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteStep, setDeleteStep] = useState<'content' | 'confirm'>('content')
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [countsLoading, setCountsLoading] = useState(false)
  const [noticeboardCount, setNoticeboardCount] = useState(0)
  const [classesCount, setClassesCount] = useState(0)
  const [otherActivitiesCount, setOtherActivitiesCount] = useState(0)
  const [activitiesAction, setActivitiesAction] = useState<'keep' | 'delete'>('keep')

  const isDeleteConfirmed = deleteConfirmation === 'DELETE'
  const hasAnyContent = noticeboardCount + classesCount + otherActivitiesCount > 0

  const resetDeleteFlow = () => {
    setDeleteStep('content')
    setDeleteConfirmation('')
    setActivitiesAction('keep')
  }

  const loadContentCounts = async () => {
    setCountsLoading(true)
    try {
      const [nbRes, classCatRes, allActRes] = await Promise.all([
        supabase.from('noticeboard_posts').select('id', { count: 'exact', head: true }).eq('user_id', userId),
        supabase.from('activity_categories').select('id').eq('name', 'Classes').maybeSingle(),
        supabase.from('activities').select('id').eq('submitted_by', userId),
      ])
      const nb = nbRes.count ?? 0
      const allActivityIds = (allActRes.data ?? []).map((a: { id: string }) => a.id)
      let classIds: string[] = []
      if (classCatRes.data?.id && allActivityIds.length > 0) {
        const { data: relRows } = await supabase
          .from('activity_category_relations')
          .select('activity_id')
          .eq('category_id', classCatRes.data.id)
          .in('activity_id', allActivityIds)
        classIds = (relRows ?? []).map((r: { activity_id: string }) => r.activity_id)
      }
      const classSet = new Set(classIds)
      const cls = classSet.size
      const others = allActivityIds.filter((id) => !classSet.has(id)).length
      setNoticeboardCount(nb)
      setClassesCount(cls)
      setOtherActivitiesCount(others)
      // If no content at all, skip step 1
      if (nb + cls + others === 0) {
        setDeleteStep('confirm')
      } else {
        setDeleteStep('content')
      }
    } catch (err) {
      console.error('Failed to load content counts:', err)
      // Fall back to confirm step so we don't block deletion
      setDeleteStep('confirm')
    } finally {
      setCountsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      const { error } = await supabase.functions.invoke('delete-account', {
        body: { activitiesAction: otherActivitiesCount > 0 ? activitiesAction : 'keep' },
      })
      if (error) throw error
      await supabase.auth.signOut({ scope: 'local' })
      toast.success("Your account has been permanently deleted")
      window.location.href = '/'
    } catch (error: any) {
      console.error('Error deleting account:', error)
      toast.error(error.message || 'Failed to delete account')
      setDeleting(false)
    }
  }

  // Profile & roles are loaded via React Query above (cached across sheet opens)

  const isAdmin = userRoles.some(role => role.role === 'admin')

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' })
    } catch (error: any) {
      console.warn('Sign out completed with warning:', error?.message)
    }
    toast.success("Signed out successfully")
    window.location.href = '/'
  }

  if (loading) {
    return (
      <div className="w-full max-w-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-muted" />
            <div className="space-y-2 flex-1">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="h-3 bg-muted rounded w-1/2" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="w-full max-w-sm p-6 text-center">
        <p className="text-muted-foreground mb-4 text-sm">Profile not found.</p>
        <Button onClick={handleSignOut} variant="outline" size="sm" className="w-full">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    )
  }

  const handleAvatarUpdate = (newAvatarUrl: string | null) => {
    setProfileOverride(prev => (prev ?? fetchedProfile) ? { ...(prev ?? fetchedProfile as Profile), avatar_url: newAvatarUrl } : null)
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'parent':
        return 'bg-primary/10 text-primary border-0'
      case 'nanny':
        return 'bg-accent/10 text-accent border-0'
      case 'childminder':
        return 'bg-secondary text-secondary-foreground border-0'
      default:
        return 'bg-secondary text-secondary-foreground border-0'
    }
  }

  return (
    <div className="w-full max-w-sm">
      {/* Profile Header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-center gap-4">
          <ProfilePictureUpload
            userId={profile.id}
            currentAvatarUrl={profile.avatar_url}
            firstName={profile.first_name}
            onAvatarUpdate={handleAvatarUpdate}
          />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-foreground truncate">
              {profile.first_name}
            </h2>
            <p className="text-xs text-muted-foreground truncate">{profile.email}</p>
            <div className="flex gap-1.5 mt-1.5">
              <Badge variant="outline" className={`text-[10px] leading-4 font-medium px-2 py-0 ${getRoleColor(profile.role)}`}>
                {getRoleLabel(profile.role, profile.is_business_owner)}
              </Badge>
              {profile.is_business_owner && profile.role !== 'other' && (
                <Badge variant="outline" className="text-[10px] leading-4 font-medium px-2 py-0 bg-accent/10 text-accent border-0">
                  Business Owner
                </Badge>
              )}
              {isAdmin && (
                <Badge variant="outline" className="text-[10px] px-2 py-0 bg-destructive/10 text-destructive border-0">
                  <Shield className="h-2.5 w-2.5 mr-0.5" />
                  Admin
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Workspace switch sits under the avatar and name so the profile still leads the menu */}
      {canUseBusiness && (
        <div className="px-5 pb-4">
          {inBusiness ? (
            <WorkspaceSwitch
              to="/activities"
              label="Switch to Member"
              hint="Browse activities and meet other families"
              onClick={() => {
                setMode("member")
                onNavigate?.()
              }}
            />
          ) : (
            <WorkspaceSwitch
              to="/business"
              label="Switch to Business"
              hint="Manage your listings, ads and meet-ups"
              onClick={onNavigate}
            />
          )}
        </div>
      )}

      {/* Divider + Navigation Menu — hidden when there are no member links (business-only accounts on the member site) */}
      {(inBusiness || profile.role !== 'other') && (
        <>
          <div className="h-px bg-border mx-5" />
          <div className="px-3 py-2 space-y-0.5">
            {inBusiness ? (
              <MenuLink to="/business?tab=profile" icon={User} label="Profile" onClick={onNavigate} />
            ) : (
              <>
                {profile.role !== 'other' && <MenuLink to={`/user/${profile.id}`} icon={User} label="View Profile" onClick={onNavigate} />}
                {profile.role !== 'other' && <MenuLink to="/interested" icon={Star} label="Interested" onClick={onNavigate} />}
                {profile.role !== 'other' && <MenuLink to="/my-bookings" icon={Ticket} label="My Bookings" onClick={onNavigate} />}
                {profile.role !== 'other' && <MenuLink to="/my-play-dates" icon={CalendarHeart} label="My Meet-ups" onClick={onNavigate} />}
                {!canUseBusiness && <MenuLink to="/admin" icon={Send} label="My Activities" onClick={onNavigate} />}
                {profile.role !== 'other' && <MenuLink to="/my-noticeboard" icon={ClipboardList} label="My Noticeboard" onClick={onNavigate} />}
              </>
            )}
          </div>
        </>
      )}

      {/* Admin */}
      {isAdmin && (
        <>
          <div className="h-px bg-border mx-5" />
          <div className="px-3 py-2">
            <MenuLink to="/admin" icon={Shield} label="Admin Panel" onClick={onNavigate} />
            <MenuLink to="/member-perks" icon={Ticket} label="Member Perks · Preview" onClick={onNavigate} />
          </div>
        </>
      )}

      {/* Settings & Sign Out */}
      <div className="h-px bg-border mx-5" />
      <div className="px-3 py-2 space-y-0.5">
        <MenuLink to={inBusiness || profile.role === 'other' ? "/business?tab=settings" : "/settings"} icon={Settings} label="Settings" onClick={onNavigate} />
        <MenuLink to="/help" icon={LifeBuoy} label="Help & Support" onClick={onNavigate} />
        <button
          onClick={handleSignOut}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg hover:bg-muted/80 transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <LogOut className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-sm font-medium text-muted-foreground">Sign Out</span>
        </button>
      </div>

      {/* Delete Account */}
      <div className="h-px bg-border mx-5" />
      <div className="px-5 py-4">
        <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
          setShowDeleteDialog(open)
          if (open) {
            resetDeleteFlow()
            loadContentCounts()
          } else {
            resetDeleteFlow()
          }
        }}>
          <AlertDialogTrigger asChild>
            <button className="text-xs text-destructive hover:text-destructive/80 underline-offset-2 hover:underline transition-colors">
              Delete account
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
            {deleteStep === 'content' ? (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>What happens to your content?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Before you delete your account, here's what will happen to the things you've shared.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="space-y-3 text-sm">
                  {countsLoading ? (
                    <p className="text-muted-foreground">Loading your content…</p>
                  ) : (
                    <>
                      {noticeboardCount > 0 && (
                        <div className="rounded-lg border p-3">
                          <p className="font-medium text-foreground">
                            Noticeboard listings ({noticeboardCount})
                          </p>
                          <p className="text-muted-foreground mt-1">Will be deleted.</p>
                        </div>
                      )}

                      {classesCount > 0 && (
                        <div className="rounded-lg border p-3">
                          <p className="font-medium text-foreground">
                            Classes ({classesCount})
                          </p>
                          <p className="text-muted-foreground mt-1">
                            Will be deleted. If you'd like to transfer ownership to someone else instead,
                            email <a href="mailto:support@balhambabes.co.uk" className="text-primary underline">support@balhambabes.co.uk</a> before
                            deleting your account.
                          </p>
                        </div>
                      )}

                      {otherActivitiesCount > 0 && (
                        <div className="rounded-lg border p-3 space-y-2">
                          <p className="font-medium text-foreground">
                            Activities &amp; cafés ({otherActivitiesCount})
                          </p>
                          <RadioGroup
                            value={activitiesAction}
                            onValueChange={(v) => setActivitiesAction(v as 'keep' | 'delete')}
                            className="space-y-2"
                          >
                            <div className="flex items-start gap-2">
                              <RadioGroupItem value="keep" id="act-keep" className="mt-0.5" />
                              <Label htmlFor="act-keep" className="font-normal cursor-pointer">
                                <span className="font-medium">Keep them live on Balham Babes</span>
                                <span className="block text-muted-foreground text-xs mt-0.5">
                                  They'll stay listed, anonymised — families can still find them.
                                </span>
                              </Label>
                            </div>
                            <div className="flex items-start gap-2">
                              <RadioGroupItem value="delete" id="act-delete" className="mt-0.5" />
                              <Label htmlFor="act-delete" className="font-normal cursor-pointer">
                                <span className="font-medium">Delete them all</span>
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <Button
                    onClick={() => setDeleteStep('confirm')}
                    disabled={countsLoading}
                  >
                    Continue
                  </Button>
                </AlertDialogFooter>
              </>
            ) : (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Account?</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                      This action cannot be undone. This will permanently delete your
                      account and all associated data including your profile, favourites,
                      notifications, friend connections, and will unsubscribe you from
                      the newsletter.
                    </p>
                    {otherActivitiesCount > 0 && (
                      <p className="text-foreground">
                        <span className="font-medium">Your activities &amp; cafés:</span>{' '}
                        {activitiesAction === 'keep'
                          ? 'will stay live (anonymised).'
                          : 'will be deleted.'}
                      </p>
                    )}
                    <div className="pt-2">
                      <label className="text-sm font-medium text-foreground">
                        Type DELETE to confirm:
                      </label>
                      <Input
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="DELETE"
                        className="mt-2"
                        autoComplete="off"
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  {hasAnyContent && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteStep('content')}
                      disabled={deleting}
                    >
                      Back
                    </Button>
                  )}
                  <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={!isDeleteConfirmed || deleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleting ? 'Deleting...' : 'Delete Account'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}